import COLORS from '../../../utils/constants/colors'

// Shared with the row so every dot and spinner can lock its phase to the same clock.
export const LIVE_PULSE_MS = 1800
export const SPIN_MS = 800

// The row above the calendar that used to hold the "New Event" button: a single summary line
// saying something is live, which expands into one controllable row per running stream.
const calendarLiveStreamsStyles = {
  '@keyframes livePulse': {
    '0%': { boxShadow: '0 0 0 0 rgba(240, 78, 99, 0.55)' },
    '70%': { boxShadow: '0 0 0 7px rgba(240, 78, 99, 0)' },
    '100%': { boxShadow: '0 0 0 0 rgba(240, 78, 99, 0)' },
  },
  '@keyframes spin': {
    to: { transform: 'rotate(360deg)' },
  },
  '@keyframes rowIn': {
    from: { opacity: 0, transform: 'translateY(-4px)' },
    to: { opacity: 1, transform: 'none' },
  },
  section: {
    marginTop: 10,
    border: `1px solid ${COLORS.borderGrey}`,
    borderRadius: 16,
    backgroundColor: COLORS.white,
    boxShadow: '0 1px 3px rgba(46, 44, 51, 0.08)',
    overflow: 'hidden',
  },
  summary: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: '12px 16px',
    background: 'none',
    border: 0,
    font: 'inherit',
    textAlign: 'left',
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: COLORS.backgroundGrey,
    },
  },
  summaryLabel: {
    fontSize: 15,
    fontWeight: 700,
    color: COLORS.lightBlack,
  },
  spacer: {
    flex: 1,
  },
  toggleLabel: {
    fontSize: 13,
    color: COLORS.grey,
  },
  chevron: {
    width: 18,
    height: 18,
    fill: COLORS.grey,
  },
  rows: {
    borderTop: `1px solid ${COLORS.searchGrey}`,
    display: 'flex',
    flexDirection: 'column',
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 14,
    padding: '12px 16px',
    borderBottom: `1px solid ${COLORS.searchGrey}`,
    animation: '$rowIn 180ms ease-out both',
    '&:last-child': {
      borderBottom: 0,
    },
  },
  dot: {
    width: 8,
    height: 8,
    borderRadius: '50%',
    flex: '0 0 auto',
    display: 'inline-block',
  },
  dotLive: {
    backgroundColor: COLORS.liveRed,
    animation: `$livePulse ${LIVE_PULSE_MS}ms ease-out infinite`,
  },
  dotPaused: {
    backgroundColor: COLORS.pausedAmber,
  },
  dotStarting: {
    backgroundColor: COLORS.broadcastPurpleLight,
  },
  dotStopping: {
    backgroundColor: COLORS.borderGrey,
  },
  titleColumn: {
    minWidth: 180,
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
  },
  title: {
    fontSize: 14,
    fontWeight: 700,
    color: COLORS.lightBlack,
  },
  timeRange: {
    fontSize: 13,
    color: COLORS.grey,
  },
  badge: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: 118,
    padding: '4px 11px',
    borderRadius: 999,
    fontSize: 12,
    fontWeight: 500,
    whiteSpace: 'nowrap',
  },
  badgeBroadcast: {
    backgroundColor: COLORS.broadcastPurpleTint,
    color: COLORS.broadcastPurple,
  },
  badgeRecording: {
    backgroundColor: COLORS.backgroundGrey,
    color: COLORS.menuChoiceGrey,
  },
  transient: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    padding: '7px 4px',
    fontSize: 13,
    fontWeight: 500,
    color: COLORS.grey,
  },
  spinner: {
    width: 14,
    height: 14,
    borderRadius: '50%',
    border: `2px solid ${COLORS.borderGrey}`,
    display: 'inline-block',
    animation: `$spin ${SPIN_MS}ms linear infinite`,
  },
  spinnerStarting: {
    borderTopColor: COLORS.broadcastPurple,
  },
  spinnerStopping: {
    borderTopColor: COLORS.liveRed,
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  button: {
    display: 'flex',
    alignItems: 'center',
    gap: 7,
    padding: '7px 14px',
    borderRadius: 999,
    border: `1px solid ${COLORS.borderGrey}`,
    backgroundColor: COLORS.white,
    color: COLORS.menuChoiceGrey,
    font: 'inherit',
    fontSize: 13,
    fontWeight: 500,
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: COLORS.backgroundGrey,
      borderColor: COLORS.borderGreyDark,
    },
    '&:disabled': {
      opacity: 0.5,
      cursor: 'default',
    },
  },
  stopButton: {
    borderColor: COLORS.liveRed,
    color: COLORS.liveRed,
    '&:hover': {
      backgroundColor: COLORS.liveRedTint,
      borderColor: COLORS.liveRed,
    },
  },
  buttonIcon: {
    width: 15,
    height: 15,
    fill: 'currentColor',
  },
  confirm: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '5px 6px 5px 14px',
    borderRadius: 999,
    backgroundColor: COLORS.liveRedTint,
  },
  confirmText: {
    fontSize: 13,
    fontWeight: 500,
    color: COLORS.liveRedDeep,
  },
  confirmCancel: {
    padding: '5px 12px',
    borderRadius: 999,
    border: `1px solid ${COLORS.borderGrey}`,
    backgroundColor: COLORS.white,
    color: COLORS.menuChoiceGrey,
    font: 'inherit',
    fontSize: 13,
    fontWeight: 500,
    cursor: 'pointer',
  },
  confirmStop: {
    padding: '6px 14px',
    borderRadius: 999,
    border: 0,
    backgroundColor: COLORS.liveRed,
    color: COLORS.white,
    font: 'inherit',
    fontSize: 13,
    fontWeight: 500,
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: COLORS.redDark,
    },
    '&:disabled': {
      opacity: 0.5,
      cursor: 'default',
    },
  },
}

export default calendarLiveStreamsStyles
